import { Suspense } from 'react';
import { Route, Navigate, Routes } from 'react-router-dom';
import * as urls from 'routes/config';
import Spinner from 'components/Common/Spinner';

import BusinessProfileForm from 'pages/Business/Setting/BusinessProfile';
import TeamMembers from 'pages/Business/Setting/TeamMembers';
import TeamMemberForm from 'pages/Business/Setting/TeamMembers/components/TeamMemberForm';
import Contacts from 'pages/Business/Setting/Contacts';
import KnowledgeBases from 'pages/Business/Setting/KnowledgeBase/KnowledgeBases';
import KnowledgeBaseForm from 'pages/Business/Setting/KnowledgeBase/components/KnowledgeBaseForm';
import KnowledgeBase from 'pages/Business/Setting/KnowledgeBase/KnowledgeBase';
import DataSet from 'pages/Business/Setting/DataSet';
import DataSetForm from 'pages/Business/Setting/DataSet/Components/DataSetForm';
import Chunks from 'pages/Business/Setting/Chunks';
import ChunkForm from 'pages/Business/Setting/Chunks/Components/ChunkForm';
import RetrievalTesting from 'pages/Business/Setting/RetrievalTesting';
import Integrations from 'pages/Business/Setting/Integrations';
import WebUrlForm from 'pages/Business/Setting/DataSet/Components/WebUrlForm';
import AddIntegrationForm from 'pages/Business/Setting/Integrations/Components/AddIntegrationForm';
import Locations from 'pages/Business/Setting/Locations';
import LocationForm from 'pages/Business/Setting/Locations/components/LocationForm';
import AuditTrails from 'pages/Business/Setting/AuditTrails';
import Usage from 'pages/Business/Setting/Usage';
import Billing from '@/pages/Business/Setting/Billing';
import PhoneNumbers from '@/pages/Business/Setting/PhoneNumbers';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { useGetCurrentUser } from '@/apis/user/hook';
import AlternativeNameForm from '@/pages/Business/Setting/Integrations/Components/StreamOrderSettings/components/AlternativeNameFrom';
import UpsellCrossSellForm from '@/pages/Business/Setting/Integrations/Components/StreamOrderSettings/components/UpsellCrossSellForm';
import QuestionAnswerForm from '@/pages/Business/Setting/DataSet/Components/QuestionAnswerForm';
import ConfigureIntegrationForm from '@/pages/Business/Setting/Integrations/Components/ConfigureIntegrationForm';
import Workflows from '@/pages/Business/Setting/Workflows';
import ApiKeys from '@/pages/Business/Setting/ApiKeys';
import ApiKeyForm from '@/pages/Business/Setting/ApiKeys/components/ApiKeyForm';
import Addons from '@/pages/Business/Setting/Addons';
import { useAiWorkflowAddonActive } from '@/pages/Business/Setting/hooks/useAiWorkflowAddonActive';

export default function BusinessSettingRoutes() {
  const { data: businessProfile, isLoading: isBusinessLoading } = useGetBusinessProfile();
  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUser();
  const { aiWorkflowPresent, isLoading: isAddonLoading } = useAiWorkflowAddonActive();
  const isLocationAdmin = currentUser?.role === 'location_admin';

  if (isBusinessLoading || isCurrentUserLoading || isAddonLoading) {
    return null;
  }

  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route index element={<Navigate to={urls.business.profile} replace />} />
        <Route path={urls.business.profile} element={<BusinessProfileForm agencyView={false} />} />
        {Boolean(!businessProfile?.hide_billing && businessProfile?.business_plan_id) && (
          <Route path={urls.business.billing} element={<Billing />} />
        )}
        <Route path={urls.business.auditTrails} element={<AuditTrails />} />
        <Route path={urls.business.usage} element={<Usage />} />
        <Route path={urls.business.telephony} element={<Navigate to='numbers' replace />} />
        <Route path={urls.business.telephonyTab()} element={<PhoneNumbers />} />
        <Route
          path={urls.business.phoneNumbers}
          element={<Navigate to={`${urls.business.base}/${urls.business.telephony}/numbers`} replace />}
        />
        <Route
          path={urls.business.phoneNumbersTab()}
          element={<Navigate to={`${urls.business.base}/${urls.business.telephony}/numbers`} replace />}
        />

        {/* Location Routes */}
        {!isLocationAdmin && (
          <>
            <Route path={urls.business.locations} element={<Locations />} />
            <Route path={urls.business.addLocation} element={<LocationForm />} />
            <Route path={urls.business.editLocation()} element={<LocationForm />} />
          </>
        )}
        {/* Integration Routes */}
        <Route path={urls.business.integration} element={<Integrations />}>
          <Route path='add' element={<AddIntegrationForm />} />
        </Route>
        <Route path={urls.business.configureIntegration()} element={<ConfigureIntegrationForm />}>
          <Route path='alternative-names/add' element={<AlternativeNameForm />} />
          <Route path='alternative-names/:alternativeNameId' element={<AlternativeNameForm />} />
          <Route path='upsell-and-cross-sell/add' element={<UpsellCrossSellForm />} />
          <Route path='upsell-and-cross-sell/:upSellAndCrossSellId' element={<UpsellCrossSellForm />} />
        </Route>
        {aiWorkflowPresent && <Route path={urls.business.workflows} element={<Workflows />} />}
        <Route path={urls.business.apiKeys} element={<ApiKeys />}>
          <Route path='add' element={<ApiKeyForm />} />
        </Route>
        <Route path={urls.business.addons} element={<Addons />} />

        {/* Team Routes */}
        <Route path={urls.business.team} element={<TeamMembers />} />
        <Route path={urls.business.addTeam} element={<TeamMemberForm />} />
        <Route path={urls.business.editTeam()} element={<TeamMemberForm />} />

        {/* Contacts Routes */}
        <Route path={urls.business.contacts} element={<Contacts />} />

        {/* Knowledge Base Routes */}
        <Route path={urls.business.KnowledgeBases} element={<KnowledgeBases />}>
          <Route path='add' element={<KnowledgeBaseForm />} />
          <Route path=':knowledgeBaseId/edit' element={<KnowledgeBaseForm />} />
          <Route path=':knowledgeBaseId/clone' element={<KnowledgeBaseForm />} />
        </Route>
        <Route path={urls.business.KnowledgeBase} element={<KnowledgeBase />}>
          <Route index element={<Navigate to={urls.knowledgeBase.dataset} replace />} />
          <Route path={urls.knowledgeBase.dataset} element={<DataSet />}>
            <Route path='add' element={<DataSetForm />} />
            <Route path='add-website' element={<WebUrlForm />} />
            <Route path=':datasetId/qa-chunk/add' element={<QuestionAnswerForm />} />
            <Route path=':datasetId/qa-chunk/:chunkId' element={<QuestionAnswerForm />} />
            <Route path=':datasetId/edit' element={<DataSetForm />} />
            <Route path=':datasetId/edit-website' element={<WebUrlForm />} />
            <Route path={`${urls.knowledgeBase.chunks()}`} element={<Chunks />}>
              <Route path='add' element={<ChunkForm />} />
              <Route path=':chunkId/edit' element={<ChunkForm />} />
            </Route>
          </Route>
          <Route path={urls.knowledgeBase.retrievalTesting} element={<RetrievalTesting />} />
        </Route>

        {/* Fallback Route */}
        <Route path='*' element={<Navigate to='/404' replace />} />
      </Routes>
    </Suspense>
  );
}
